<template>
  <div>
    <b-card no-body class="mb-1">
      <b-card-body>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Status" label-for="snapshot-change-status">
              <v-select
                id="snapshot-change-status"
                v-model="status"
                :options="statusOptions"
                :reduce="(option) => option.value"
                label="text"
                placeholder="Todos"
              >
                <template #no-options>Nenhum status encontrado</template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Contrato" label-for="snapshot-change-contract">
              <b-form-input
                id="snapshot-change-contract"
                v-model="contractId"
                type="number"
                placeholder="ID do contrato"
                @keyup.enter="search(1)"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            class="d-flex align-items-end justify-content-end mt-1 mb-1"
          >
            <b-button
              variant="outline-secondary"
              class="mr-1"
              :disabled="loading"
              @click.prevent="clearFilters"
            >
              Limpar filtros
            </b-button>
            <b-button
              variant="outline-primary"
              :disabled="loading"
              @click.prevent="search(1)"
            >
              <b-spinner v-if="loading" small />
              <feather-icon v-else icon="SearchIcon" class="mr-50" />
              <span>{{ loading ? "Aguarde..." : "Pesquisar" }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="4"
            class="d-flex align-items-center justify-content-start"
          >
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search(currentPage)"
            >
              <template #no-options>Sem resultados</template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>
        </b-row>
      </div>

      <b-table
        :items="items"
        :fields="fields"
        :busy="loading"
        responsive
        show-empty
        empty-text="Nenhuma solicitação encontrada"
        class="position-relative"
      >
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ statusLabel(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(contract_id)="data">
          <b-link :to="contractRoute(data.item.contract_id)" target="_blank">
            {{ data.item.contract_id }}
          </b-link>
        </template>

        <template #cell(proposed_total_percentage)="data">
          <!-- A soma pode ser menor que 100: o resto fica com a Futuro. -->
          <span :class="{ 'text-warning': !isHundred(data.item.proposed_total_percentage) }">
            {{ percentage(data.item.proposed_total_percentage) }}
          </span>
        </template>

        <template #cell(created_at)="data">
          {{ dateTime(data.item.created_at) }}
        </template>

        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="outline-primary"
            @click="openRequest(data.item)"
          >
            {{ canDecide(data.item) ? "Analisar" : "Detalhes" }}
          </b-button>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Exibindo {{ pageItemsFrom }} à {{ pageItemsTo }} de {{ totalItems }} itens
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              :value="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="search"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <snapshot-change-request-sidebar @decided="search(currentPage)" />
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BLink,
  BPagination,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { contractPermissions } from "@/mixins/contractPermissions";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import { apiErrorToast } from "@/helpers/error_messages";
import * as sharedTypes from "@/modules/shared/store/types";
import {
  REQUEST_STATUS,
  REQUEST_STATUS_OPTIONS,
  statusLabel,
  statusVariant,
} from "@/constants/snapshot_change";
import SnapshotChangeRequestSidebar from "../components/SnapshotChangeRequestSidebar";
import * as types from "../store/types";

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BLink,
    BPagination,
    BSpinner,
    vSelect,
    SnapshotChangeRequestSidebar,
  },
  mixins: [contractPermissions],
  data() {
    return {
      loading: false,
      status: undefined,
      contractId: undefined,
      currentPage: 1,
      itemsPerPage: 10,
      statusOptions: REQUEST_STATUS_OPTIONS,
      fields: [
        { key: "contract_id", label: "Contrato" },
        { key: "status", label: "Status" },
        { key: "proposed_total_percentage", label: "Total proposto" },
        { key: "requested_by_name", label: "Solicitante" },
        { key: "created_at", label: "Solicitado em" },
        { key: "actions", label: "Ações" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      searchResult: types.REQUESTS_SEARCH_RESULT,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    items() {
      return this.searchResult?.data || [];
    },
    totalItems() {
      return this.searchResult?.total || 0;
    },
    pageItemsFrom() {
      return this.searchResult?.from || 0;
    },
    pageItemsTo() {
      return this.searchResult?.to || 0;
    },
  },
  setup() {
    return { toast: useToast() };
  },
  mounted() {
    this.itemsPerPage = this.initialItemsPerPage;
    if (this.$route.query.contract_id) {
      this.contractId = this.$route.query.contract_id;
    }
    this.search(1);
  },
  methods: {
    ...mapActions({
      searchRequests: types.SEARCH_REQUESTS,
      openRequestSidebar: types.OPEN_REQUEST_SIDEBAR,
    }),
    statusLabel,
    statusVariant,
    canDecide(request) {
      return (
        this.canApproveSnapshotChange &&
        request.status === REQUEST_STATUS.PENDING
      );
    },
    clearFilters() {
      this.status = undefined;
      this.contractId = undefined;
      this.search(1);
    },
    search(page) {
      this.currentPage = page || 1;
      this.loading = true;

      this.searchRequests({
        status: this.status || undefined,
        contract_id: this.contractId || undefined,
        limit: this.itemsPerPage,
        page: this.currentPage,
      })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              "Não foi possível carregar as solicitações. Entre em contato com o setor de TI."
            ),
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openRequest(request) {
      this.openRequestSidebar({ request });
    },
    contractRoute(contractId) {
      return `/contrato/${contractId}/detalhes/comissoes`;
    },
    isHundred(value) {
      return Math.abs(Number(value) - 100) <= 0.0001;
    },
    percentage(value) {
      return `${Number(value || 0).toFixed(2).replace(".", ",")}%`;
    },
    dateTime(value) {
      if (!value) return "—";

      return new Date(value).toLocaleString("pt-BR");
    },
  },
};
</script>
